import { type ReactNode, useId, useRef } from 'react';
import styled from 'styled-components';
import { useIsTextTruncated } from '@ardoq/hooks';
import { colors } from '@ardoq/design-tokens';
import { ControlledPopover } from './ControlledPopover';
import { PlainTextPopover } from './PlainTextPopover';
import {
  type DefinitionContent,
  renderDefinitionContent,
} from './DefinitionPopover';
import { useDelayedHover } from './useDelayedHover';

const EllipsisText = styled.div<{ $hasHiddenContent?: boolean }>`
  display: inline-block;
  ${({ $hasHiddenContent }) => $hasHiddenContent && 'cursor: pointer;'}
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${colors.borderFocus};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

export type TruncatedTextPopoverProps = {
  children: string;
  className?: string;
  /**
   * Custom content to render in the popover instead of the plain truncated
   * text, e.g. a richer rendering. Defaults to the child text.
   */
  content?: DefinitionContent;
  /**
   * Opens the popover even when the text is not truncated, for cases where the
   * popover shows something the preview text cannot represent.
   */
  hasHiddenContent?: boolean;
  /**
   * Rendered inside the hover target, ahead of the text. A marker for content
   * the text cannot show belongs here rather than beside the component, so that
   * it is hoverable when the text itself is empty.
   */
  prefix?: ReactNode;
};

/**
 * Renders text truncated with an ellipsis, and shows a popover with the full
 * text on hover — but only when it is actually truncated. The popover is
 * controlled internally (hover to open, hover-out or a click on the text to
 * close), so it dismisses on click too (unlike a self-managed hover popover).
 *
 * The minimal case is just the text as a child:
 * `<TruncatedTextPopover>{label}</TruncatedTextPopover>`.
 */
export const TruncatedTextPopover = ({
  children,
  className,
  content,
  hasHiddenContent = false,
  prefix,
}: TruncatedTextPopoverProps) => {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const isTruncated = useIsTextTruncated(ref);
  const isPopoverAvailable = isTruncated || hasHiddenContent;
  const { isOpen, handleMouseEnter, handleMouseLeave, handleClose } =
    useDelayedHover();

  const popoverContent =
    content !== undefined && content !== null && content !== false ? (
      () => {
        const rendered = renderDefinitionContent(content);
        return rendered ? <>{rendered}</> : null;
      }
    ) : (
      <PlainTextPopover>{children}</PlainTextPopover>
    );

  return (
    <ControlledPopover
      id={id}
      isOpen={isOpen && isPopoverAvailable}
      onClose={handleClose}
      onClickOutside={handleClose}
      onMouseEnterPopover={handleMouseEnter}
      onMouseLeavePopover={handleMouseLeave}
      content={popoverContent}
    >
      <EllipsisText
        ref={ref}
        $hasHiddenContent={hasHiddenContent}
        className={className}
        tabIndex={isPopoverAvailable ? 0 : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleClose}
        onClick={handleClose}
      >
        {prefix}
        {children}
      </EllipsisText>
    </ControlledPopover>
  );
};
